// packages/utils/spheres/src/lib/updateSphere.ts

import { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere } from 'types/global';
import { assignSubjectToPerspective } from './perspectives/assignSubjectToPerspective';
import { invalidateSphereById } from './getSphereById';

export interface UpdateSphereParams {
  sphereId: string;
  oldSphere: Sphere;
  updatedSphere: Sphere;
}

export interface UpdateSphereResponse {
  success: boolean;
  status: string;
  sphere: Sphere;
  ancestorIds?: string[];
}

export class UpdateSphereError extends Error {
  details: string | string[];
  constructor(message: string, details: string | string[]) {
    super(message);
    this.details = details;
    this.name = 'UpdateSphereError';
  }
}

/**
 * Value equality for a sphere property, for change detection only.
 *
 * Property values are scalars, dates, or JSON-shaped arrays/objects (the graph
 * stores the latter as JSON), so a structural comparison is both sufficient and
 * cheap. `undefined` and `null` are treated as the same "absent" value: the read
 * path omits absent keys while a form supplies `null` for a cleared field, and
 * that difference must not register as an edit.
 */
export function isSamePropertyValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  if (a instanceof Date || b instanceof Date) {
    const at = a instanceof Date ? a.getTime() : new Date(a as string).getTime();
    const bt = b instanceof Date ? b.getTime() : new Date(b as string).getTime();
    return Number.isNaN(at) && Number.isNaN(bt) ? false : at === bt;
  }

  if (typeof a !== 'object' || typeof b !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => isSamePropertyValue(item, b[i]));
  }

  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(b, k) &&
      isSamePropertyValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
  );
}

/**
 * Update an existing sphere: diffs properties and newly assigned perspectives,
 * sends PATCH for changed properties, and calls assignSubjectToPerspective for each new assignment.
 */
// updateSphere.ts
export async function updateSphere(
  apiClient: ApiClient,
  params: UpdateSphereParams
): Promise<UpdateSphereResponse> {
  const { sphereId, oldSphere, updatedSphere } = params;
  if (!sphereId || !oldSphere || !updatedSphere) {
    throw new Error('sphereId, oldSphere and updatedSphere are required');
  }

  // 1) Diff properties
  //
  // BY VALUE, not by reference. The previous `!==` compared object identity, so
  // every array- or object-valued property counted as changed on EVERY save even
  // when the user touched nothing — the form's value bag is a fresh clone of the
  // sphere's. That put read-only, system-managed keys such as `ancestor_ids` into
  // the PATCH body, and `PATCH /spheres/:id/properties` rejects the whole request
  // with `Property "ancestor_ids" is read-only and cannot be modified directly`.
  // Renaming a sphere therefore failed outright. Dates are compared by instant so
  // an equal date parsed into two Date objects is not a spurious change.
  const oldProps = oldSphere.properties ?? {};
  const newProps = updatedSphere.properties ?? {};
  const changedProps: Record<string, unknown> = {};
  for (const key of Object.keys(newProps)) {
    if (!isSamePropertyValue(oldProps[key], newProps[key])) {
      changedProps[key] = newProps[key];
    }
  }

  // 2) Diff newly assigned perspectives
  const oldAssigned = oldSphere.assignedPerspectives ?? [];
  const newAssigned = updatedSphere.assignedPerspectives ?? [];
  const toAssign = newAssigned.flatMap(np => {
    const matching = oldAssigned.find(
      op => op.perspectiveProperties.system_name === np.perspectiveProperties.system_name
    );
    const oldIds = new Set(matching?.assigned.map(a => a.sphere.id));
    const newIds = np.assigned.map(a => a.sphere.id).filter(id => !oldIds.has(id));
    return newIds.map(id => ({
      systemName: np.perspectiveProperties.system_name,
      subjectId: id
    }));
  });

  // 3) Perform perspective assignments
  for (const { systemName, subjectId } of toAssign) {
    const resp = await assignSubjectToPerspective(apiClient, {
      perspectiveSystemName: systemName,
      sphereId,
      subjectId
    });
    if (!resp.success) {
      throw new Error(`Assign to ${systemName} failed: ${resp.message}`);
    }
  }

  // 4) If nothing to patch, stop here
  if (Object.keys(changedProps).length === 0) {
    return { success: true, status: 'No changes detected', sphere: oldSphere };
  }

  // 5) Send PATCH
  const patchResponse = await apiClient.patch<{ status: string; sphere?: Sphere; ancestorIds?: string[] }>(
    `/spheres/${encodeURIComponent(sphereId)}/properties`,
    changedProps
  );

  // The full-sphere read cache would otherwise serve the pre-edit vertex for the
  // remainder of its TTL.
  invalidateSphereById(sphereId);

  return {
    success: true,
    status: 'Sphere updated',
    sphere: updatedSphere,
    ancestorIds: patchResponse?.ancestorIds
  };
}
